import { useState, useContext, useEffect, useCallback, useRef, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { FaCheck, FaBoxes, FaWarehouse, FaQrcode, FaList, FaSync, FaFilter, FaUserAlt, FaTimes } from 'react-icons/fa';
import { DateTime } from 'luxon';

import useLogisticsApi from '../../hooks/use-logistics-api';
import { useWarehouseIncomingQuery, usePartnerUsersQuery, useWarehouseIncomingInvalidation, queryKeys } from '../../hooks/use-queries';
import { useDriverMap } from '../../hooks/use-driver-map';
import { useQueryBridge } from '../../hooks/use-query-bridge';
import useReader from '../../hooks/use-reader';
import { flattenOrdersToBoxes } from '../../utils/order-transforms';
import { groupBoxesByOrder } from '../../utils/box-grouping';

import AppContext from '../../store/app-context';
import AuthContext from '../../store/auth-context';

import { ActionBar, ActionButton } from '../../components/Shared/ActionBar/ActionBar';
import BarcodeDisplay from '../../components/Shared/BarcodeDisplay/BarcodeDisplay';
import { barcodeShortLabel } from '../../utils/barcode-format';

import './Incoming.scss';
import {
    EnhancedOrderBoxWithContext,
    BoxStatusCode,
    getWarehouseNextStatus,
    STATUS_CODE_DISPLAY_NAMES,
    STATUS_CODE_ORDER
} from '../../types/logistics';
import { AUTO_STORE_ON_RECEIVE, SHOW_WAREHOUSE_LOCATION_MODAL } from '../../config/constants';

interface SectionData {
    boxes: EnhancedOrderBoxWithContext[];
    completedBoxes: EnhancedOrderBoxWithContext[];
    total: number;
    completed: number;
}

const Incoming = () => {
    const queryClient = useQueryClient();
    const [manualMode, setManualMode] = useState(false);
    const [expandedSection, setExpandedSection] = useState<'receive' | 'store' | null>('receive');
    const [selectedOrderId, setSelectedOrderId] = useState<number | null>(null);

    // Filter state
    const today = DateTime.now().toFormat('yyyy-MM-dd');
    const [selectedDate, setSelectedDate] = useState(today);
    const [selectedDriverId, setSelectedDriverId] = useState<number | null>(null);
    const [showFilters, setShowFilters] = useState(false);

    // All boxes from API (with order context) — local state for optimistic scan updates
    const [allBoxes, setAllBoxes] = useState<EnhancedOrderBoxWithContext[]>([]);

    // Warehouse location modal state (for WKKU→WKKS store transition)
    const [showLocationModal, setShowLocationModal] = useState(false);
    const [locationInput, setLocationInput] = useState('');
    const [pendingStoreBox, setPendingStoreBox] = useState<EnhancedOrderBoxWithContext | null>(null);

    const appCtx = useContext(AppContext);
    const authCtx = useContext(AuthContext);

    const { updateBoxStatus } = useLogisticsApi();
    const { invalidateWarehouseIncoming } = useWarehouseIncomingInvalidation();
    const { startQuagga } = useReader(null);

    // Per-box duplicate guard — prevent same box being processed concurrently
    const processingBoxIds = useRef(new Set<number>());

    // Server data via React Query (cached, deduplicated)
    const { data: rawOrders, isLoading: ordersLoading, isSuccess: ordersLoaded, error: ordersError } = useWarehouseIncomingQuery(selectedDate);
    const { data: partnerUsers } = usePartnerUsersQuery(authCtx.partner?.id);

    const hasActiveFilters = selectedDate !== today || selectedDriverId !== null;

    // Refs for latest values
    const geolocationRef = useRef(appCtx.geolocation);

    useEffect(() => {
        geolocationRef.current = appCtx.geolocation;
    }, [appCtx.geolocation]);

    // Filter boxes by selected driver (use collectionDriverId, fall back to assignedDriverId)
    const filteredBoxes = useMemo(() => {
        if (selectedDriverId === null) return allBoxes;
        return allBoxes.filter(b => (b.collectionDriverId ?? b.assignedDriverId) === selectedDriverId);
    }, [allBoxes, selectedDriverId]);

    const filteredBoxesRef = useRef(filteredBoxes);
    useEffect(() => {
        filteredBoxesRef.current = filteredBoxes;
    }, [filteredBoxes]);

    // Calculate section data from filtered boxes
    // ITTKKFR (failed returns) show in receive section - warehouse receives them back → ITKK
    const sections = useMemo((): { receive: SectionData; store: SectionData } => {
        const receiveBoxes = filteredBoxes.filter(b => b.statusCode === 'ITKK' || b.statusCode === 'ITTKKFR');
        const storeBoxes = filteredBoxes.filter(b => b.statusCode === 'WKKU');
        const storedBoxes = filteredBoxes.filter(b => b.statusCode === 'WKKS');

        if (AUTO_STORE_ON_RECEIVE) {
            // Merged mode: WKKU boxes (mid-chain failures) stay as pending, WKKS = completed receives
            return {
                receive: {
                    boxes: [...receiveBoxes, ...storeBoxes],
                    completedBoxes: storedBoxes,
                    total: receiveBoxes.length + storeBoxes.length,
                    completed: [...receiveBoxes, ...storeBoxes].filter(b => b.scanned).length
                },
                store: { boxes: [], completedBoxes: [], total: 0, completed: 0 }
            };
        }

        return {
            receive: {
                boxes: receiveBoxes,
                completedBoxes: [],
                total: receiveBoxes.length,
                completed: receiveBoxes.filter(b => b.scanned).length
            },
            store: {
                boxes: storeBoxes,
                completedBoxes: storedBoxes,
                total: storeBoxes.length,
                completed: storeBoxes.filter(b => b.scanned).length
            }
        };
    }, [filteredBoxes]);

    // Driver name lookup (cached via React Query, shared with Dispatch page)
    const { driverMap, getDriverName } = useDriverMap(partnerUsers);

    // Derive available drivers from loaded boxes (only shows drivers with actual orders)
    const availableDrivers = useMemo(() => {
        const driverIds = new Set<number>();
        allBoxes.forEach(b => {
            const driverId = b.collectionDriverId ?? b.assignedDriverId;
            if (driverId !== null) driverIds.add(driverId);
        });
        return Array.from(driverIds).map(id => ({
            id,
            name: driverMap.get(id)?.name || `Driver #${id}`
        })).sort((a, b) => a.name.localeCompare(b.name));
    }, [allBoxes, driverMap]);

    // Execute the store transition with optional location
    const executeStoreTransition = useCallback(async (box: EnhancedOrderBoxWithContext, warehouseLocation?: string) => {
        if (processingBoxIds.current.has(box.id)) return;
        processingBoxIds.current.add(box.id);
        const geo = geolocationRef.current;
        try {
            await updateBoxStatus(box.id, 'WKKS', {
                latitude: geo.latitude ? parseFloat(geo.latitude) : undefined,
                longitude: geo.longitude ? parseFloat(geo.longitude) : undefined,
                warehouseLocation: warehouseLocation || undefined
            });

            setAllBoxes(prev => prev.map(b => {
                if (b.id === box.id) {
                    return { ...b, statusCode: 'WKKS' as any, scanned: false };
                }
                return b;
            }));
            invalidateWarehouseIncoming(selectedDate);

            const locationMsg = warehouseLocation ? ` (${warehouseLocation})` : '';
            appCtx.newMessage({
                text: `Box ${barcodeShortLabel(box.barcode, box.id, box.barcodeComponents)} → Stored${locationMsg}`,
                type: 1
            });
        } catch (error: any) {
            appCtx.newMessage({
                text: error.message || 'Failed to store box',
                type: 0
            });
        } finally {
            processingBoxIds.current.delete(box.id);
        }
    }, [updateBoxStatus, appCtx, invalidateWarehouseIncoming, selectedDate]);

    // Smart scan handler - auto-detects box status and applies correct transition
    // When AUTO_STORE_ON_RECEIVE is on, chains through WKKU to WKKS in one operation
    const handleBoxScanned = useCallback(async (box: EnhancedOrderBoxWithContext) => {
        if (processingBoxIds.current.has(box.id)) return;

        const nextStatus = getWarehouseNextStatus(box.statusCode);

        if (!nextStatus) {
            appCtx.newMessage({
                text: `Box ${barcodeShortLabel(box.barcode, box.id, box.barcodeComponents)} cannot be processed in incoming`,
                type: 0
            });
            return;
        }

        // For WKKU→WKKS (store), show location modal if enabled
        if (SHOW_WAREHOUSE_LOCATION_MODAL && box.statusCode === 'WKKU' && nextStatus === 'WKKS') {
            setPendingStoreBox(box);
            setLocationInput('');
            setShowLocationModal(true);
            return;
        }

        processingBoxIds.current.add(box.id);
        const geo = geolocationRef.current;
        const geoPayload = {
            latitude: geo.latitude ? parseFloat(geo.latitude) : undefined,
            longitude: geo.longitude ? parseFloat(geo.longitude) : undefined
        };

        try {
            // Step 1: Execute the immediate next transition
            await updateBoxStatus(box.id, nextStatus, geoPayload);
            let finalStatus: BoxStatusCode = nextStatus;

            // Step 2: If auto-store is on, chain through to WKKS
            if (AUTO_STORE_ON_RECEIVE && finalStatus !== 'WKKS') {
                let currentStatus: BoxStatusCode = finalStatus;
                while (currentStatus !== ('WKKS' as BoxStatusCode)) {
                    const chainNext = getWarehouseNextStatus(currentStatus);
                    if (!chainNext || chainNext === currentStatus) break;
                    // Safety: don't chain past WKKS into pick/dispatch
                    if (STATUS_CODE_ORDER[chainNext] > STATUS_CODE_ORDER['WKKS']) break;

                    await updateBoxStatus(box.id, chainNext, geoPayload);
                    currentStatus = chainNext;
                    finalStatus = chainNext;
                }
            }

            // Update local state with the final status
            setAllBoxes(prev => prev.map(b => {
                if (b.id === box.id) {
                    return { ...b, statusCode: finalStatus, scanned: false };
                }
                return b;
            }));
            invalidateWarehouseIncoming(selectedDate);

            appCtx.newMessage({
                text: `Box ${barcodeShortLabel(box.barcode, box.id, box.barcodeComponents)} → ${STATUS_CODE_DISPLAY_NAMES[finalStatus]}`,
                type: 1
            });
        } catch (error: any) {
            // Mid-chain failure: re-fetch server state so box appears at its actual status
            console.warn('Failed during box transition chain:', error);
            invalidateWarehouseIncoming(selectedDate);
            appCtx.newMessage({
                text: error.message || 'Failed to update box',
                type: 0
            });
        } finally {
            processingBoxIds.current.delete(box.id);
        }
    }, [updateBoxStatus, appCtx, invalidateWarehouseIncoming, selectedDate]);

    // Update box scan state (for scanner integration)
    const updateBox = useCallback(async (boxOrLocation: any) => {
        // If it's a location string, ignore for incoming (no location needed)
        if (typeof boxOrLocation === 'string') return;

        const box = boxOrLocation as EnhancedOrderBoxWithContext;

        // Update local state
        setAllBoxes(prev => prev.map(b => {
            if (b.id === box.id || b.barcode === box.barcode) {
                return { ...b, scanned: box.scanned };
            }
            return b;
        }));

        // If box was scanned, trigger status update
        if (box.scanned) {
            await handleBoxScanned(box);
        }
    }, [handleBoxScanned]);

    // Sync query data → local allBoxes state (transform orders to flat box list).
    // Warehouse filter: order.assignedPartnerId must match the logged-in user's
    // partnerId (user.partnerId === partner.id). All warehouse staff in the same
    // partner org share one warehouse, so they see the same set of orders.
    // Orders with NULL assignedPartnerId are unassigned and excluded.
    const myPartnerId = authCtx.user?.partnerId ?? null;
    useEffect(() => {
        if (!rawOrders) return;
        const partnerOrders = myPartnerId != null
            ? rawOrders.filter(o => o.assignedPartnerId === myPartnerId)
            : rawOrders;
        setAllBoxes(flattenOrdersToBoxes(partnerOrders, ['ITKK', 'WKKU', 'ITTKKFR', 'WKKS']));
    }, [rawOrders, myPartnerId]);

    // Bridge loading/error states to global overlay
    useQueryBridge(ordersLoading, ordersError as Error | null, 'Loading incoming boxes...', 'Failed to load incoming boxes', !!rawOrders);

    // Handle scanning - if an order is selected, scan only that order's boxes
    const handleStartScan = useCallback(async () => {
        setManualMode(false);
        let boxesToScan = filteredBoxesRef.current.filter(b =>
            b.statusCode === 'ITKK' || b.statusCode === 'WKKU' || b.statusCode === 'ITTKKFR'
        );
        if (selectedOrderId !== null) {
            boxesToScan = boxesToScan.filter(b => b.orderId === selectedOrderId);
        }
        await startQuagga({
            boxes: boxesToScan,
            update: updateBox
        });
    }, [startQuagga, updateBox, selectedOrderId]);

    // Handle manual toggle
    const handleManualToggle = useCallback((box: EnhancedOrderBoxWithContext) => {
        if (!box.scanned) {
            appCtx.newDialog({
                text: `Process box ${barcodeShortLabel(box.barcode, box.id, box.barcodeComponents)}?`,
                confirm: async () => {
                    await handleBoxScanned(box);
                }
            });
        }
    }, [appCtx, handleBoxScanned]);

    const clearFilters = useCallback(() => {
        setSelectedDate(today);
        setSelectedDriverId(null);
    }, [today]);

    const dateFormatted = selectedDate === today
        ? DateTime.now().toFormat('EEEE, d MMMM yyyy')
        : DateTime.fromISO(selectedDate).toFormat('EEEE, d MMMM yyyy');

    const totalBoxes = AUTO_STORE_ON_RECEIVE
        ? sections.receive.total + sections.receive.completedBoxes.length
        : sections.receive.total + sections.store.total;
    // Scannable = pending boxes only (not completed WKKS)
    const scannableBoxes = sections.receive.total + sections.store.total;
    const totalOrders = new Set([
        ...sections.receive.boxes.map(b => b.orderId),
        ...sections.receive.completedBoxes.map(b => b.orderId),
        ...sections.store.boxes.map(b => b.orderId)
    ]).size;
    const receiveOrders = new Set(sections.receive.boxes.map(b => b.orderId)).size;
    const receivedOrders = new Set(sections.receive.completedBoxes.map(b => b.orderId)).size;
    const storeOrders = new Set(sections.store.boxes.map(b => b.orderId)).size;

    const groupBoxes = useCallback((boxes: EnhancedOrderBoxWithContext[]) =>
        groupBoxesByOrder(boxes, getDriverName, 'collectionDriverId'),
    [getDriverName]);

    const renderBoxCard = (box: EnhancedOrderBoxWithContext, statusClass: string) => {
        // Completed boxes (WKKS) are not actionable — don't make them tappable
        const isActionable = box.statusCode !== 'WKKS';
        return (
        <div
            key={box.id}
            className={`box-item ${manualMode && isActionable ? 'tappable' : ''} ${box.scanned ? 'scanned' : ''}`}
            onClick={() => manualMode && isActionable && handleManualToggle(box)}
        >
            <div className="barcode">
                <BarcodeDisplay barcode={box.barcode} boxId={box.id} barcodeComponents={box.barcodeComponents} />
            </div>
            {box.scanned ? (
                <div className="status-badge scanned-badge">✓ Scanned</div>
            ) : (
                <div className={`status-badge ${statusClass}`}>
                    {STATUS_CODE_DISPLAY_NAMES[box.statusCode]}
                </div>
            )}
        </div>
    );
    };

    return (
        <div className="warehouse-incoming">
            {/* Header */}
            <div className="page-header">
                <div className="header-content">
                    <FaWarehouse className="header-icon" />
                    <div className="header-text">
                        <h1>Incoming</h1>
                        <span className="date">{dateFormatted}</span>
                    </div>
                </div>
                <button className="btn-refresh" onClick={() => queryClient.invalidateQueries({ queryKey: queryKeys.warehouseIncoming(selectedDate) })}>
                    <FaSync />
                </button>
            </div>

            {/* Filter Bar */}
            <div className="filter-bar">
                <button
                    className={`btn-filter-toggle ${showFilters ? 'active' : ''} ${hasActiveFilters ? 'has-filters' : ''}`}
                    onClick={() => setShowFilters(!showFilters)}
                >
                    <FaFilter />
                    <span>Filters</span>
                    {hasActiveFilters && <span className="filter-badge" />}
                </button>

                {showFilters && (
                    <div className="filter-controls">
                        <div className="filter-field">
                            <label>Date</label>
                            <input
                                type="date"
                                value={selectedDate}
                                max={today}
                                onChange={(e) => {
                                    setSelectedDate(e.target.value);
                                }}
                            />
                        </div>
                        <div className="filter-field">
                            <label>Courier</label>
                            <select
                                value={selectedDriverId ?? ''}
                                onChange={(e) => setSelectedDriverId(e.target.value ? Number(e.target.value) : null)}
                            >
                                <option value="">All Couriers</option>
                                {availableDrivers.map(d => (
                                    <option key={d.id} value={d.id}>{d.name}</option>
                                ))}
                            </select>
                        </div>
                        {hasActiveFilters && (
                            <button className="btn-clear-filters" onClick={clearFilters}>
                                <FaTimes />
                                <span>Clear Filters</span>
                            </button>
                        )}
                    </div>
                )}
            </div>

            {/* Summary Cards */}
            <div className="summary-strip">
                <div className="summary-item">
                    <span className="label">Orders</span>
                    <span className="value">{totalOrders}</span>
                    <span className="sub-label">{totalBoxes} boxes</span>
                </div>
                <div className="summary-item receive">
                    <span className="label">{AUTO_STORE_ON_RECEIVE ? 'Pending' : 'To Receive'}</span>
                    <span className="value">{sections.receive.total}</span>
                    {receiveOrders > 0 && <span className="sub-label">{receiveOrders} order{receiveOrders !== 1 ? 's' : ''}</span>}
                </div>
                {AUTO_STORE_ON_RECEIVE ? (
                    <div className="summary-item store">
                        <span className="label">Received & Stored</span>
                        <span className="value">{sections.receive.completedBoxes.length}</span>
                        {receivedOrders > 0 && <span className="sub-label">{receivedOrders} order{receivedOrders !== 1 ? 's' : ''}</span>}
                    </div>
                ) : (
                    <div className="summary-item store">
                        <span className="label">To Store</span>
                        <span className="value">{sections.store.total}</span>
                        {storeOrders > 0 && <span className="sub-label">{storeOrders} order{storeOrders !== 1 ? 's' : ''}</span>}
                    </div>
                )}
            </div>

            {/* Sections */}
            <div className="workflow-sections">
                {/* Receive Section */}
                <div
                    className={`workflow-section ${expandedSection === 'receive' ? 'expanded' : ''} ${sections.receive.total === 0 ? 'complete' : ''}`}
                    onClick={() => setExpandedSection(expandedSection === 'receive' ? null : 'receive')}
                >
                    <div className="section-header">
                        <div className="section-icon receive">
                            <FaBoxes />
                        </div>
                        <div className="section-info">
                            <h3>{AUTO_STORE_ON_RECEIVE ? 'Receive & Store' : 'Receive'}</h3>
                            <span className="status-text">{AUTO_STORE_ON_RECEIVE ? 'Scan to receive and store' : 'ITKK / ITTKKFR → WKKU'}</span>
                        </div>
                        <div className="section-progress">
                            {sections.receive.total === 0 ? (
                                <FaCheck className="complete-icon" />
                            ) : (
                                <span className="count">{sections.receive.total}</span>
                            )}
                        </div>
                    </div>

                    {expandedSection === 'receive' && (sections.receive.boxes.length > 0 || sections.receive.completedBoxes.length > 0) && (
                        <div className="section-content" onClick={e => e.stopPropagation()}>
                            {groupBoxes(sections.receive.boxes).map(group => (
                                <div
                                    key={group.orderId}
                                    className={`order-group ${selectedOrderId === group.orderId ? 'selected' : ''}`}
                                    onClick={() => setSelectedOrderId(selectedOrderId === group.orderId ? null : group.orderId)}
                                >
                                    <div className="order-group-header">
                                        <span className="customer-name">#{group.orderId} — {group.customerName}</span>
                                        <span className="group-meta">
                                            <FaUserAlt /> {group.driverName}
                                            <span className="box-count">{group.boxes.length} box{group.boxes.length !== 1 ? 'es' : ''}</span>
                                        </span>
                                        {selectedOrderId === group.orderId && (
                                            <span className="selected-badge">Selected</span>
                                        )}
                                    </div>
                                    <div className="box-grid">
                                        {group.boxes.map((box) => renderBoxCard(box, box.statusCode === 'WKKU' ? 'wkku' : 'itkk'))}
                                    </div>
                                </div>
                            ))}
                            {sections.receive.completedBoxes.length > 0 && (
                                <>
                                    <div className="completed-divider">
                                        <FaCheck /> <span>Received & Stored</span>
                                    </div>
                                    {groupBoxes(sections.receive.completedBoxes).map(group => (
                                        <div key={`done-${group.orderId}`} className="order-group completed-group">
                                            <div className="order-group-header">
                                                <span className="customer-name">#{group.orderId} — {group.customerName}</span>
                                                <span className="group-meta">
                                                    <span className="box-count">{group.boxes.length} box{group.boxes.length !== 1 ? 'es' : ''}</span>
                                                </span>
                                            </div>
                                            <div className="box-grid">
                                                {group.boxes.map((box) => renderBoxCard(box, 'wkks'))}
                                            </div>
                                        </div>
                                    ))}
                                </>
                            )}
                        </div>
                    )}
                </div>

                {/* Store Section — hidden when AUTO_STORE_ON_RECEIVE is on */}
                {!AUTO_STORE_ON_RECEIVE && (
                    <div
                        className={`workflow-section ${expandedSection === 'store' ? 'expanded' : ''} ${sections.store.total === 0 ? 'complete' : ''}`}
                        onClick={() => setExpandedSection(expandedSection === 'store' ? null : 'store')}
                    >
                        <div className="section-header">
                            <div className="section-icon store">
                                <FaWarehouse />
                            </div>
                            <div className="section-info">
                                <h3>Stored</h3>
                                <span className="status-text">WKKU → WKKS</span>
                            </div>
                            <div className="section-progress">
                                {sections.store.total === 0 ? (
                                    <FaCheck className="complete-icon" />
                                ) : (
                                    <span className="count">{sections.store.total}</span>
                                )}
                            </div>
                        </div>

                        {expandedSection === 'store' && (sections.store.boxes.length > 0 || sections.store.completedBoxes.length > 0) && (
                            <div className="section-content" onClick={e => e.stopPropagation()}>
                                {groupBoxes(sections.store.boxes).map(group => (
                                    <div
                                        key={group.orderId}
                                        className={`order-group ${selectedOrderId === group.orderId ? 'selected' : ''}`}
                                        onClick={() => setSelectedOrderId(selectedOrderId === group.orderId ? null : group.orderId)}
                                    >
                                        <div className="order-group-header">
                                            <span className="customer-name">#{group.orderId} — {group.customerName}</span>
                                            <span className="group-meta">
                                                <FaUserAlt /> {group.driverName}
                                                <span className="box-count">{group.boxes.length} box{group.boxes.length !== 1 ? 'es' : ''}</span>
                                            </span>
                                            {selectedOrderId === group.orderId && (
                                                <span className="selected-badge">Selected</span>
                                            )}
                                        </div>
                                        <div className="box-grid">
                                            {group.boxes.map((box) => renderBoxCard(box, 'wkku'))}
                                        </div>
                                    </div>
                                ))}
                                {sections.store.completedBoxes.length > 0 && (
                                    <>
                                        <div className="completed-divider">
                                            <FaCheck /> <span>Stored</span>
                                        </div>
                                        {groupBoxes(sections.store.completedBoxes).map(group => (
                                            <div key={`done-${group.orderId}`} className="order-group completed-group">
                                                <div className="order-group-header">
                                                    <span className="customer-name">#{group.orderId} — {group.customerName}</span>
                                                    <span className="group-meta">
                                                        <span className="box-count">{group.boxes.length} box{group.boxes.length !== 1 ? 'es' : ''}</span>
                                                    </span>
                                                </div>
                                                <div className="box-grid">
                                                    {group.boxes.map((box) => renderBoxCard(box, 'wkks'))}
                                                </div>
                                            </div>
                                        ))}
                                    </>
                                )}
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Empty State */}
            {totalBoxes === 0 && ordersLoaded && (
                <div className="empty-state">
                    <FaBoxes className="empty-icon" />
                    <p>
                        {hasActiveFilters
                            ? `No incoming boxes match filters for ${DateTime.fromISO(selectedDate).toFormat('d MMMM yyyy')}`
                            : 'No incoming boxes expected today'
                        }
                    </p>
                </div>
            )}

            {/* Selected order indicator */}
            {selectedOrderId !== null && (
                <div className="selected-order-bar">
                    <span>Order #{selectedOrderId} selected</span>
                    <button onClick={() => setSelectedOrderId(null)}>
                        <FaTimes /> Clear
                    </button>
                </div>
            )}

            {/* Action Bar */}
            <ActionBar colorScheme="warn-active">
                <ActionButton variant="primary" icon={<FaQrcode />} onClick={handleStartScan} disabled={scannableBoxes === 0}>
                    {selectedOrderId !== null ? `Scan #${selectedOrderId}` : 'Scan All'}
                </ActionButton>
                <ActionButton variant={manualMode ? 'active' : 'default'} icon={<FaList />} onClick={() => setManualMode(!manualMode)} disabled={scannableBoxes === 0}>
                    {manualMode ? 'Exit Manual' : 'Manual'}
                </ActionButton>
            </ActionBar>

            {/* Warehouse location modal — shown when storing boxes (WKKU→WKKS) */}
            {showLocationModal && pendingStoreBox && (
                <div className="location-modal">
                    <div className="modal-backdrop" onClick={() => {
                        setShowLocationModal(false);
                        setPendingStoreBox(null);
                    }} />
                    <div className="modal-content">
                        <h3>Storage Location</h3>
                        <p>
                            Storing box {barcodeShortLabel(pendingStoreBox.barcode, pendingStoreBox.id, pendingStoreBox.barcodeComponents)}
                        </p>
                        <input
                            type="text"
                            className="location-input"
                            placeholder="e.g., A1-03, Shelf B2"
                            value={locationInput}
                            onChange={(e) => setLocationInput(e.target.value)}
                            autoFocus
                        />
                        <span className="location-hint">Optional — leave blank if locations aren't set up yet</span>
                        <div className="modal-actions">
                            <button className="btn-skip" onClick={async () => {
                                setShowLocationModal(false);
                                await executeStoreTransition(pendingStoreBox);
                                setPendingStoreBox(null);
                            }}>
                                Skip
                            </button>
                            <button className="btn-confirm" onClick={async () => {
                                setShowLocationModal(false);
                                await executeStoreTransition(pendingStoreBox, locationInput.trim() || undefined);
                                setPendingStoreBox(null);
                            }}>
                                <FaCheck /> Store
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Incoming;
